import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getInfoPage, saveInfoPage, deleteInfoSection, uploadInfoImage, deleteInfoImage } from '../../services/api';
import { FiEdit2, FiSave, FiX, FiTrash2, FiUpload, FiArrowLeft, FiEyeOff, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Toast from '../../components/Toast';
import AuthImg from '../../components/AuthImg';

// One "folder" in the Information hub. Reads the slug from the URL, lets admins
// rename / re-icon / edit content / toggle visibility / delete the section, and
// upload or remove photos. Members see read-only.
const InfoPage = ({ basePath }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [page, setPage] = useState({ title: '', icon: '', description: '', content: '', visible: 1, updated_at: null });
  const [images, setImages] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', icon: '', description: '', content: '', visible: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
  const [dragOver, setDragOver] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [toast, setToast] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const fileRef = useRef(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => {
    getInfoPage(slug).then(r => {
      const p = r.data?.page || {};
      setPage({
        title: p.title || '',
        icon: p.icon || '',
        description: p.description || '',
        content: p.content || '',
        visible: p.visible == null ? 1 : p.visible,
        updated_at: p.updated_at || null,
      });
      setImages(Array.isArray(r.data?.images) ? r.data.images : []);
      setNotFound(false);
    }).catch(err => {
      if (err.response?.status === 404) setNotFound(true);
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  // Lightbox keyboard nav — Esc closes, arrows move, only while open.
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      else if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % images.length);
      else if (e.key === 'ArrowLeft')  setLightboxIdx(i => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, images.length]);

  const startEdit = () => {
    setForm({
      title: page.title, icon: page.icon, description: page.description,
      content: page.content, visible: !!page.visible,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveInfoPage(slug, form);
      setPage(p => ({ ...p, ...form, visible: form.visible ? 1 : 0 }));
      setEditing(false);
      showToast('Saved');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the "${page.title}" section and all its photos? This cannot be undone.`)) return;
    try {
      await deleteInfoSection(slug);
      navigate(basePath);
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  // Upload many photos at once. Sequential (not parallel) so the server isn't
  // hammered + so progress UI stays accurate. Failures are tallied so the toast
  // tells the user "N of M uploaded" even when some images are too big / rejected.
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && f.type && f.type.startsWith('image/'));
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let ok = 0, failed = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const fd = new FormData();
        fd.append('image', files[i]);
        await uploadInfoImage(slug, fd);
        ok++;
      } catch (err) { failed++; }
      setUploadProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    setUploadProgress(null);
    if (fileRef.current) fileRef.current.value = '';
    load();
    if (failed === 0) showToast(`${ok} photo${ok === 1 ? '' : 's'} added`);
    else if (ok === 0) showToast(`Upload failed (${failed} rejected)`, 'error');
    else showToast(`${ok} added, ${failed} failed`, 'error');
  };

  const handleUpload = (e) => uploadFiles(e.target.files);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!isAdmin) return;
    uploadFiles(e.dataTransfer.files);
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Remove this image?')) return;
    try { await deleteInfoImage(id); setImages(prev => prev.filter(i => i.id !== id)); }
    catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  if (notFound) {
    return (
      <div className="page-content">
        <div className="page-header">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(basePath)} style={{ marginBottom: 12 }}>
            <FiArrowLeft /> Information
          </button>
          <h1>Section not found</h1>
          <p>It may have been removed or hidden.</p>
        </div>
      </div>
    );
  }

  const hasContent = (page.content && page.content.trim()) || images.length > 0;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(basePath)}>
          <FiArrowLeft /> Information
        </button>
      </div>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 40, lineHeight: 1 }}>{page.icon || '📄'}</div>
          <div>
            <h1 style={{ margin: 0 }}>
              {page.title}
              {!page.visible && (
                <span style={{ marginLeft: 10, background: '#fef3c7', color: '#a16207', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FiEyeOff size={11} /> Hidden from members
                </span>
              )}
            </h1>
            {page.description && <p style={{ marginTop: 4, color: '#6b7280' }}>{page.description}</p>}
          </div>
        </div>
        {isAdmin && !editing && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={startEdit}><FiEdit2 /> Edit</button>
            <button className="btn-icon danger" onClick={handleDelete} title="Delete section"><FiTrash2 /></button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="card" style={{ maxWidth: 820 }}>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Icon</label>
                <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="📜" maxLength={4} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Title</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="form-group form-full">
                <label>Short note</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="form-group form-full">
                <label>Content</label>
                <textarea
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={14}
                  style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}
                />
              </div>
              <div className="form-group form-full">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.visible} onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} />
                  Visible to members
                </label>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}><FiSave /> {saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}><FiX /> Cancel</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card" style={{ maxWidth: 820 }}>
          <div className="card-body">
            {hasContent ? (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.7, color: '#374151' }}>{page.content}</div>
            ) : (
              <div className="empty-state"><p>This section is empty.</p></div>
            )}
            {page.updated_at && (
              <div style={{ marginTop: 14, fontSize: 11, color: '#9ca3af' }}>Last updated: {String(page.updated_at).slice(0, 16).replace('T', ' ')}</div>
            )}
          </div>
        </div>
      )}

      {/* Image gallery — drop zone wraps the whole grid so dragging
          photos anywhere onto it triggers an upload. */}
      <div
        style={{ maxWidth: 820, marginTop: 18 }}
        onDragOver={(e) => { if (isAdmin) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#374151' }}>
            Photo gallery ({images.length})
            {uploadProgress && (
              <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, color: '#2563eb' }}>
                Uploading {uploadProgress.done} of {uploadProgress.total}…
              </span>
            )}
          </h3>
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} style={{ display: 'none' }} />
              <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <FiUpload /> {uploading ? 'Uploading…' : 'Add photos'}
              </button>
            </>
          )}
        </div>

        {/* Drop hint when admin drags a file over the gallery */}
        {isAdmin && dragOver && (
          <div style={{
            border: '2px dashed #2563eb', borderRadius: 12, padding: '32px 16px',
            textAlign: 'center', background: '#eff6ff', color: '#1d4ed8',
            fontSize: 14, fontWeight: 600, marginBottom: 12,
          }}>
            Drop photos here to upload
          </div>
        )}

        {images.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
            {images.map((img, idx) => (
              <div key={img.id} style={{ position: 'relative', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                <div
                  onClick={() => setLightboxIdx(idx)}
                  style={{ cursor: 'zoom-in' }}
                  title="Click to view full size"
                >
                  <AuthImg filename={img.filename} alt={img.caption || ''} style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} fallback={<div style={{ width: '100%', height: 150, background: '#f3f4f6' }} />} />
                </div>
                {img.caption && <div style={{ padding: '6px 10px', fontSize: 12, color: '#6b7280' }}>{img.caption}</div>}
                {isAdmin && (
                  <button
                    onClick={() => handleDeleteImage(img.id)}
                    title="Remove"
                    style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(220,38,38,0.92)', color: '#fff', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : !dragOver ? (
          <div
            className="empty-state"
            style={{
              padding: '32px 16px', textAlign: 'center',
              border: isAdmin ? '2px dashed #d1d5db' : '1px dashed #e5e7eb',
              borderRadius: 12, background: '#fafafa',
            }}
          >
            <p style={{ color: '#9ca3af', margin: 0 }}>
              {isAdmin ? 'No photos yet. Click "Add photos" or drag images here.' : 'No photos yet.'}
            </p>
          </div>
        ) : null}
      </div>

      {/* Lightbox — full-screen viewer with prev/next + delete */}
      {lightboxIdx !== null && images[lightboxIdx] && (
        <div
          onClick={() => setLightboxIdx(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            title="Close (Esc)"
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 8, width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <FiX size={20} />
          </button>
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + images.length) % images.length); }}
                title="Previous (←)"
                style={{ position: 'absolute', left: 16, background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 8, width: 48, height: 48, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <FiChevronLeft size={28} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % images.length); }}
                title="Next (→)"
                style={{ position: 'absolute', right: 16, background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 8, width: 48, height: 48, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <FiChevronRight size={28} />
              </button>
            </>
          )}
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '92vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <AuthImg
              filename={images[lightboxIdx].filename}
              alt={images[lightboxIdx].caption || ''}
              style={{ maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 8, background: '#000' }}
              fallback={<div style={{ width: 400, height: 300, background: '#333' }} />}
            />
            <div style={{ color: '#e5e7eb', fontSize: 13, textAlign: 'center' }}>
              {images[lightboxIdx].caption && <div style={{ marginBottom: 4 }}>{images[lightboxIdx].caption}</div>}
              <div style={{ color: '#9ca3af', fontSize: 11 }}>{lightboxIdx + 1} of {images.length}</div>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default InfoPage;
